import React, { memo, MouseEvent, useCallback } from "react";
import {
    FinancialDocument,
    FinancialDocumentExtended,
} from "@common/types/financialDocument";
import { DescriptionList } from "@design-system/DescriptionList/DescriptionList";
import { ImportedStatementIcon, MatchedIcon, ReconciledIcon } from "@icons";
import {
    DocumentFlowStatus,
    getDocumentStatusIconColorClass,
    getImportStatus,
    getImportStatusLabel,
    getMatchStatus,
    getMatchStatusLabel,
    getReconciliationStatus,
    getReconciliationStatusLabel,
    hasTransactionMatches,
} from "./lib";
import { DocumentStatusIconCallbacks } from "./DocumentStatusIcons";
import metadataStyles from "./FinancialDocumentDetails/FinancialDocumentMetadata.module.scss";

interface Props extends DocumentStatusIconCallbacks {
    document: FinancialDocument | FinancialDocumentExtended;
}

interface StatusEntry {
    key: string;
    status: NonNullable<DocumentFlowStatus>;
    Icon: React.FC<React.SVGProps<SVGSVGElement>>;
    label: string;
    ariaLabel: string;
}

function getStatusEntries(
    document: FinancialDocument | FinancialDocumentExtended,
): StatusEntry[] {
    const entries: StatusEntry[] = [];

    const importStatus = getImportStatus(document);
    if (importStatus) {
        entries.push({
            key: "import",
            status: importStatus,
            Icon: ImportedStatementIcon,
            label: getImportStatusLabel(importStatus),
            ariaLabel: "View import status",
        });
    }

    const reconciliationStatus = getReconciliationStatus(document);
    if (reconciliationStatus) {
        entries.push({
            key: "reconciliation",
            status: reconciliationStatus,
            Icon: ReconciledIcon,
            label: getReconciliationStatusLabel(reconciliationStatus),
            ariaLabel: "View reconciliation",
        });
    }

    const matchStatus = hasTransactionMatches(document)
        ? getMatchStatus(document)
        : null;
    if (matchStatus) {
        entries.push({
            key: "match",
            status: matchStatus,
            Icon: MatchedIcon,
            label: getMatchStatusLabel(matchStatus),
            ariaLabel: "View matched transaction",
        });
    }

    return entries;
}

export const DocumentStatusList = memo(
    ({
        document,
        onImportClick,
        onReconciliationClick,
        onMatchClick,
    }: Props) => {
        const entries = getStatusEntries(document);

        const getClickHandler = useCallback(
            (key: string, status: NonNullable<DocumentFlowStatus>) => {
                if (key === "import" && onImportClick) {
                    return (e: MouseEvent) => {
                        e.stopPropagation();
                        onImportClick(status);
                    };
                }

                if (key === "reconciliation" && onReconciliationClick) {
                    return (e: MouseEvent) => {
                        e.stopPropagation();
                        onReconciliationClick(status);
                    };
                }

                if (key === "match" && onMatchClick) {
                    return (e: MouseEvent) => {
                        e.stopPropagation();
                        onMatchClick(status);
                    };
                }

                return undefined;
            },
            [onImportClick, onMatchClick, onReconciliationClick],
        );

        if (entries.length === 0) {
            return null;
        }

        return (
            <DescriptionList>
                {entries.map(({ key, status, Icon, label, ariaLabel }) => {
                    const onClick = getClickHandler(key, status);
                    const iconClassName =
                        getDocumentStatusIconColorClass(status);

                    if (onClick) {
                        return (
                            <button
                                key={key}
                                type="button"
                                className={metadataStyles.inlineIconButton}
                                onClick={onClick}
                                aria-label={ariaLabel}
                            >
                                <Icon className={iconClassName} />
                                <span>{label}</span>
                            </button>
                        );
                    }

                    return (
                        <span key={key} className={metadataStyles.inlineIcon}>
                            <Icon className={iconClassName} />
                            <span>{label}</span>
                        </span>
                    );
                })}
            </DescriptionList>
        );
    },
);
DocumentStatusList.displayName = "DocumentStatusList";
